import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { OUTCOME_TYPES } from '../config/outcomeTypes';
import { AlgorithmModal } from '../pages/admin/components/AlgorithmLibrary';

const ActionModal = ({ existingAction, onClose, onSave, forceWholeFarm = false, algorithms = [], onAlgorithmCreated }) => {
    // Initialize standard form state
    const [formData, setFormData] = useState({
        name: '',
        shortDescription: '',
        longDescription: '',
        unit: 'ha',
        actionType: forceWholeFarm ? 'whole_farm_manual' : 'field_based',
        spatialAreaTargeting: '',
    });

    // Initialize dynamic outcomes state — each references a registered
    // OutcomeAlgorithm. `outcomeName` is a UI-only field (not sent to the
    // backend — it's derivable from the chosen algorithm's outcomeType) used
    // to filter the Algorithm dropdown down to matching algorithms only.
    const [outcomes, setOutcomes] = useState([
        { outcomeAlgorithmId: '', outcomeName: '' }
    ]);

    // Algorithms created inline via "+ Create new algorithm" this session —
    // merged with the `algorithms` prop so a brand-new algorithm is
    // immediately selectable without waiting for the parent to refetch.
    const [extraAlgorithms, setExtraAlgorithms] = useState([]);
    const allAlgorithms = [...algorithms, ...extraAlgorithms];

    // Index of the outcome row currently showing the inline "Create new
    // algorithm" modal, or null when none is open.
    const [createAlgorithmForRow, setCreateAlgorithmForRow] = useState(null);

    // Action Variations (optional farmer-facing sub-options, e.g. "Planting
    // Date" / "Seed Mix") — each option carries a per-outcome-algorithm
    // performance multiplier, defaulting to 1.0 when unset.
    const [variations, setVariations] = useState([]);

    // Initialize the fixed Multiplier table state
    const [multipliers, setMultipliers] = useState({
        "Arable Land": 1.00,
        "Permanent Grassland": 1.00,
        "Other": 1.00
    });

    const isWholeFarm = formData.actionType !== 'field_based';

    // Populate if editing an existing action
    useEffect(() => {
        if (existingAction) {
            setFormData({
                name: existingAction.name || '',
                shortDescription: existingAction.shortDescription || '',
                longDescription: existingAction.longDescription || '',
                unit: existingAction.unit || 'ha',
                actionType: existingAction.actionType || 'field_based',
                spatialAreaTargeting: existingAction.spatialAreaTargeting || '',
            });

            if (existingAction.outcomes && existingAction.outcomes.length > 0) {
                setOutcomes(existingAction.outcomes.map(o => ({
                    ...o,
                    outcomeName: algorithms.find(alg => alg._id === o.outcomeAlgorithmId)?.outcomeType || ''
                })));
            } else {
                setOutcomes([{ outcomeAlgorithmId: '', outcomeName: '' }]);
            }

            setVariations(existingAction.variations || []);

            // Extract existing multipliers or fallback to 1.00
            const existingMults = existingAction.parcelTypeMultipliers || {};
            setMultipliers({
                "Arable Land": existingMults["Arable Land"] !== undefined ? parseFloat(existingMults["Arable Land"]) : 1.00,
                "Permanent Grassland": existingMults["Permanent Grassland"] !== undefined ? parseFloat(existingMults["Permanent Grassland"]) : 1.00,
                "Other": existingMults["Other"] !== undefined ? parseFloat(existingMults["Other"]) : 1.00
            });
        }
    }, [existingAction, algorithms]);

    // Field-Based actions must always be Area-scaled (see the disabled
    // scaling selector below) — reset any outcomes carrying a stale 'flat'
    // value the moment the admin switches into field_based.
    useEffect(() => {
        if (formData.actionType === 'field_based') {
            setOutcomes(prev => prev.some(o => o.quantityBasis && o.quantityBasis !== 'area')
                ? prev.map(o => ({ ...o, quantityBasis: 'area' }))
                : prev);
        }
    }, [formData.actionType]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleMultiplierChange = (landUse, value) => {
        const numValue = value === '' ? 0 : parseFloat(value);
        setMultipliers(prev => ({
            ...prev,
            [landUse]: numValue
        }));
    };

    const handleAddOutcome = () => {
        if (outcomes.length >= 5) return alert("Maximum of 5 outcomes allowed.");
        setOutcomes([...outcomes, { outcomeAlgorithmId: '', outcomeName: '' }]);
    };

    const handleRemoveOutcome = (index) => {
        if (outcomes.length === 1) return alert("You must have at least one outcome.");
        setOutcomes(outcomes.filter((_, i) => i !== index));
    };

    // Changing the Outcome Name resets the algorithm choice, since a
    // previously-selected algorithm almost certainly won't match the new
    // outcome type.
    const handleOutcomeNameChange = (index, outcomeName) => {
        const newOutcomes = [...outcomes];
        newOutcomes[index] = { outcomeAlgorithmId: '', outcomeName };
        setOutcomes(newOutcomes);
    };

    const handleOutcomeAlgorithmChange = (index, outcomeAlgorithmId) => {
        const newOutcomes = [...outcomes];
        newOutcomes[index] = { ...newOutcomes[index], outcomeAlgorithmId };
        setOutcomes(newOutcomes);
    };

    // 'area': rate * bid quantity (parcel/target area or units) — the default.
    // 'flat': rate is already the total delivered across the farm (e.g. kg N
    // reduced) — no area/unit scaling. See backend/models/Action.js.
    const handleOutcomeQuantityBasisChange = (index, quantityBasis) => {
        const newOutcomes = [...outcomes];
        newOutcomes[index] = { ...newOutcomes[index], quantityBasis };
        setOutcomes(newOutcomes);
    };

    const handleAlgorithmCreated = (index, newAlgorithm) => {
        setExtraAlgorithms(prev => [...prev, newAlgorithm]);
        const newOutcomes = [...outcomes];
        newOutcomes[index] = { outcomeAlgorithmId: newAlgorithm._id, outcomeName: newAlgorithm.outcomeType };
        setOutcomes(newOutcomes);
        setCreateAlgorithmForRow(null);
        onAlgorithmCreated?.();
    };

    // --- ACTION VARIATIONS ---
    const handleAddVariation = () => {
        setVariations([...variations, { variationName: '', options: [{ optionName: '', multipliers: {} }] }]);
    };

    const handleRemoveVariation = (index) => {
        setVariations(variations.filter((_, i) => i !== index));
    };

    const handleVariationNameChange = (index, variationName) => {
        const next = [...variations];
        next[index] = { ...next[index], variationName };
        setVariations(next);
    };

    const handleAddOption = (variationIndex) => {
        const next = [...variations];
        next[variationIndex] = {
            ...next[variationIndex],
            options: [...next[variationIndex].options, { optionName: '', multipliers: {} }]
        };
        setVariations(next);
    };

    const handleRemoveOption = (variationIndex, optionIndex) => {
        const next = [...variations];
        next[variationIndex] = {
            ...next[variationIndex],
            options: next[variationIndex].options.filter((_, i) => i !== optionIndex)
        };
        setVariations(next);
    };

    const handleOptionNameChange = (variationIndex, optionIndex, optionName) => {
        const next = [...variations];
        const options = [...next[variationIndex].options];
        options[optionIndex] = { ...options[optionIndex], optionName };
        next[variationIndex] = { ...next[variationIndex], options };
        setVariations(next);
    };

    const handleMultiplierValueChange = (variationIndex, optionIndex, outcomeAlgorithmId, value) => {
        const next = [...variations];
        const options = [...next[variationIndex].options];
        const multipliers = { ...options[optionIndex].multipliers, [outcomeAlgorithmId]: value === '' ? 1.0 : parseFloat(value) };
        options[optionIndex] = { ...options[optionIndex], multipliers };
        next[variationIndex] = { ...next[variationIndex], options };
        setVariations(next);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (formData.actionType === 'field_based') {
            for (let o of outcomes) {
                if (!o.outcomeAlgorithmId) return alert("All outcomes must have a selected algorithm.");
            }
        }
        if (formData.actionType === 'whole_farm_non_manual' && !formData.spatialAreaTargeting) {
            return alert("Please select a Spatial Area Targeting option.");
        }
        const hasOutcomes = formData.actionType === 'field_based' || formData.actionType === 'whole_farm_non_manual';
        const payload = {
            ...formData,
            unit: isWholeFarm ? 'item' : formData.unit,
            spatialAreaTargeting: formData.actionType === 'whole_farm_non_manual' ? formData.spatialAreaTargeting : null,
            // outcomeName is a UI-only field for filtering the Algorithm dropdown —
            // never part of the persisted schema (see backend/models/Action.js).
            // Field-Based actions are always Area-scaled, regardless of what the
            // (disabled) selector's underlying state happens to hold.
            outcomes: hasOutcomes ? outcomes.map(({ outcomeName, ...rest }) => (
                formData.actionType === 'field_based' ? { ...rest, quantityBasis: 'area' } : rest
            )) : [],
            parcelTypeMultipliers: formData.actionType === 'field_based' ? multipliers : {},
            variations: hasOutcomes ? variations.filter(v => v.variationName.trim()) : []
        };
        onSave(payload);
    };

    const activeAlgorithmsForOutcome = (outcomeName) => allAlgorithms.filter(alg =>
        alg.outcomeType === outcomeName && (alg.status === 'active' || outcomes.some(o => o.outcomeAlgorithmId === alg._id))
    );

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={{ margin: 0, color: '#0F172A', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        {existingAction ? 'Edit Action' : forceWholeFarm ? 'Create Whole Farm Action' : 'Create Field Action'}
                    </h2>
                    <button onClick={onClose} type="button" style={styles.closeBtn}>✕</button>
                </div>

                <form onSubmit={handleSubmit} style={styles.formContent}>
                    <div style={styles.grid}>
                        <div style={styles.column}>
                            <label style={styles.label}>Action Name</label>
                            <input required type="text" name="name" value={formData.name} onChange={handleChange} style={styles.input} placeholder="e.g. Cover Crops" />

                            {!isWholeFarm && (
                                <>
                                    <label style={styles.label}>Unit Type</label>
                                    <select name="unit" value={formData.unit} onChange={handleChange} style={styles.input}>
                                        <option value="ha">Hectares (ha)</option>
                                        <option value="m">Meters (m)</option>
                                        <option value="item">Items (count)</option>
                                    </select>
                                </>
                            )}

                            {isWholeFarm && (
                                <>
                                    <label style={styles.label}>Whole Farm Type</label>
                                    <select name="actionType" value={formData.actionType} onChange={handleChange} style={styles.input}>
                                        <option value="whole_farm_manual">Manual Review</option>
                                        <option value="whole_farm_non_manual">Non-Manual (Spatial Targeting)</option>
                                    </select>
                                </>
                            )}

                            {formData.actionType === 'whole_farm_non_manual' && (
                                <>
                                    <label style={styles.label}>Spatial Area Targeting</label>
                                    <select name="spatialAreaTargeting" value={formData.spatialAreaTargeting} onChange={handleChange} style={styles.input} required>
                                        <option value="" disabled>-- Select Targeting --</option>
                                        <option value="arable_in_zone">Arable in Zone</option>
                                        <option value="arable_and_grassland_in_zone">Arable and Grassland in Zone</option>
                                    </select>
                                </>
                            )}

                            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#F8FAFC', borderRadius: '4px', border: '1px solid #E2E8F0' }}>
                                <span style={{ fontWeight: 'bold', color: '#0F172A' }}>
                                    Action Type: {formData.actionType === 'field_based' ? 'Field'
                                        : formData.actionType === 'whole_farm_manual' ? 'Whole Farm (Manual)'
                                        : 'Whole Farm (Non-Manual)'}
                                </span>
                            </div>

                            {(formData.actionType === 'field_based' || formData.actionType === 'whole_farm_non_manual') && (
                                <div style={{ marginTop: '15px', borderTop: '1px solid #E2E8F0', paddingTop: '15px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <label style={{...styles.label, margin: 0}}>Outcomes Delivered</label>
                                        {outcomes.length < 5 && (
                                            <button type="button" onClick={handleAddOutcome} style={{...styles.btnSecondary, padding: '4px 8px', fontSize: '0.75rem'}}>+ Add Outcome</button>
                                        )}
                                    </div>
                                    <span style={{ fontSize: '0.75rem', color: '#64748B', display: 'block', marginBottom: '10px' }}>
                                        Bind up to 5 outcomes. £ value per unit is set at the Event level, not here.
                                    </span>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {outcomes.map((outcome, index) => (
                                            <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center', backgroundColor: '#F8FAFC', padding: '8px', borderRadius: '4px', border: '1px solid #CBD5E1' }}>
                                                <select
                                                    value={outcome.outcomeName || ''}
                                                    onChange={(e) => handleOutcomeNameChange(index, e.target.value)}
                                                    style={{...styles.input, marginBottom: 0, flex: 1, padding: '6px'}}
                                                    required
                                                >
                                                    <option value="" disabled>-- Outcome Name --</option>
                                                    {OUTCOME_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>

                                                <select
                                                    value={outcome.outcomeAlgorithmId || ''}
                                                    onChange={(e) => {
                                                        if (e.target.value === '__create_new__') {
                                                            setCreateAlgorithmForRow(index);
                                                        } else {
                                                            handleOutcomeAlgorithmChange(index, e.target.value);
                                                        }
                                                    }}
                                                    disabled={!outcome.outcomeName}
                                                    style={{...styles.input, marginBottom: 0, flex: 2, padding: '6px'}}
                                                    required
                                                >
                                                    <option value="" disabled>-- Select Algorithm --</option>
                                                    {activeAlgorithmsForOutcome(outcome.outcomeName).map(alg => (
                                                        <option key={alg._id} value={alg._id}>
                                                            {alg.name}{alg.formulaString ? ` (${alg.formulaString})` : ''}
                                                        </option>
                                                    ))}
                                                    <option value="__create_new__">+ Create new algorithm...</option>
                                                </select>

                                                <select
                                                    value={formData.actionType === 'field_based' ? 'area' : (outcome.quantityBasis || 'area')}
                                                    onChange={(e) => handleOutcomeQuantityBasisChange(index, e.target.value)}
                                                    disabled={formData.actionType === 'field_based'}
                                                    style={{...styles.input, marginBottom: 0, flex: 1, padding: '6px'}}
                                                    title={formData.actionType === 'field_based'
                                                        ? 'Field-Based actions are always Area-scaled (rate x field area).'
                                                        : 'Area-scaled: rate x farm/field area. Flat total: rate is already the total across the farm (e.g. kg N reduced), no area scaling.'}
                                                >
                                                    <option value="area">Area-scaled</option>
                                                    <option value="flat">Flat total (no area)</option>
                                                </select>

                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveOutcome(index)}
                                                    style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '1.2rem', padding: '0 5px' }}
                                                    title="Remove Outcome"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={styles.column}>
                            <label style={styles.label}>Short Description (Requirements - ~100 words)</label>
                            <textarea name="shortDescription" value={formData.shortDescription} onChange={handleChange} style={{ ...styles.input, height: '70px', resize: 'vertical' }} placeholder="Brief summary of requirements..." />

                            <label style={styles.label}>Long Description (Details - ~750 words)</label>
                            <textarea name="longDescription" value={formData.longDescription} onChange={handleChange} style={{ ...styles.input, height: '120px', resize: 'vertical' }} placeholder="Full detailed specification..." />

                            {!isWholeFarm && (
                                <div style={{ marginTop: '5px' }}>
                                    <label style={{...styles.label, marginBottom: '2px'}}>Land Use Multiplier</label>
                                    <span style={{ fontSize: '0.75rem', color: '#64748B', display: 'block', marginBottom: '8px' }}>
                                        Set Multiplier to 0 to exclude land types. "Other" applies to all unlisted types.
                                    </span>

                                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #CBD5E1', borderRadius: '4px', overflow: 'hidden' }}>
                                        <thead style={{ backgroundColor: '#F8FAFC' }}>
                                            <tr>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.8rem', color: '#475569', borderBottom: '1px solid #CBD5E1' }}>Land Use</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '0.8rem', color: '#475569', borderBottom: '1px solid #CBD5E1', width: '120px' }}>Multiplier</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {['Arable Land', 'Permanent Grassland', 'Other'].map(type => (
                                                <tr key={type} style={{ borderBottom: '1px solid #E2E8F0', backgroundColor: '#FFFFFF' }}>
                                                    <td style={{ padding: '8px 12px', fontSize: '0.85rem', color: '#0F172A', fontWeight: type === 'Other' ? 'bold' : 'normal' }}>
                                                        {type}
                                                    </td>
                                                    <td style={{ padding: '4px 8px' }}>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={multipliers[type]}
                                                            onChange={(e) => handleMultiplierChange(type, e.target.value)}
                                                            style={{ width: '100%', padding: '6px', boxSizing: 'border-box', textAlign: 'center', border: '1px solid #CBD5E1', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.9rem' }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {(formData.actionType === 'field_based' || formData.actionType === 'whole_farm_non_manual') && (
                        <div style={{ marginTop: '20px', borderTop: '1px solid #E2E8F0', paddingTop: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <label style={{...styles.label, margin: 0}}>Action Variations <span style={{fontWeight:'normal', textTransform:'none', color:'#94A3B8'}}>(Optional)</span></label>
                                <button type="button" onClick={handleAddVariation} style={{...styles.btnSecondary, padding:'4px 8px', fontSize:'0.75rem'}}>+ Add Variation</button>
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#64748B', display: 'block', marginBottom: '10px' }}>
                                Optional farmer-facing sub-options (e.g. "Planting Date", "Seed Mix") — each option can carry a performance multiplier per outcome above (default 1.0).
                            </span>

                            {variations.map((variation, vIndex) => {
                                const boundOutcomes = outcomes.filter(o => o.outcomeAlgorithmId);
                                return (
                                    <div key={vIndex} style={{ backgroundColor:'#F8FAFC', border:'1px solid #CBD5E1', borderRadius:'4px', padding:'10px', marginBottom:'10px' }}>
                                        <div style={{ display:'flex', gap:'10px', alignItems:'center', marginBottom:'10px' }}>
                                            <input
                                                type="text"
                                                placeholder="Variation Name (e.g. Planting Date)"
                                                value={variation.variationName}
                                                onChange={(e) => handleVariationNameChange(vIndex, e.target.value)}
                                                style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                                            />
                                            <button type="button" onClick={() => handleRemoveVariation(vIndex)} style={{ background:'none', border:'none', color:'#EF4444', cursor:'pointer', fontSize:'1.1rem' }} title="Remove Variation">✕</button>
                                        </div>

                                        {variation.options.map((option, oIndex) => (
                                            <div key={oIndex} style={{ display:'flex', flexWrap:'wrap', gap:'8px', alignItems:'center', backgroundColor:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'4px', padding:'8px', marginBottom:'6px' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Option Name (e.g. Species Rich Mix)"
                                                    value={option.optionName}
                                                    onChange={(e) => handleOptionNameChange(vIndex, oIndex, e.target.value)}
                                                    style={{ ...styles.input, marginBottom: 0, flex:'1 1 160px' }}
                                                />
                                                {boundOutcomes.map(o => {
                                                    const alg = allAlgorithms.find(a => a._id === o.outcomeAlgorithmId);
                                                    return (
                                                        <div key={o.outcomeAlgorithmId} style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                                                            <span style={{ fontSize:'0.7rem', color:'#475569' }}>{alg?.outcomeType || 'Outcome'}×</span>
                                                            <input
                                                                type="number" step="0.01" min="0"
                                                                value={option.multipliers?.[o.outcomeAlgorithmId] ?? 1.0}
                                                                onChange={(e) => handleMultiplierValueChange(vIndex, oIndex, o.outcomeAlgorithmId, e.target.value)}
                                                                style={{ width:'70px', padding:'6px', border:'1px solid #CBD5E1', borderRadius:'4px', fontFamily:'monospace', textAlign:'center' }}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                                <button type="button" onClick={() => handleRemoveOption(vIndex, oIndex)} style={{ background:'none', border:'none', color:'#EF4444', cursor:'pointer' }} title="Remove Option">✕</button>
                                            </div>
                                        ))}
                                        <button type="button" onClick={() => handleAddOption(vIndex)} style={{...styles.btnSecondary, padding:'4px 8px', fontSize:'0.75rem'}}>+ Add Option</button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div style={styles.footer}>
                        <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
                        <button type="submit" style={styles.saveBtn}>{existingAction ? 'Save Changes' : forceWholeFarm ? 'Create Whole Farm Action' : 'Create Field Action'}</button>
                    </div>
                </form>
            </div>

            {createAlgorithmForRow !== null && (
                <AlgorithmModal
                    initialOutcomeType={outcomes[createAlgorithmForRow]?.outcomeName}
                    onClose={() => setCreateAlgorithmForRow(null)}
                    onSave={async (payload) => {
                        try {
                            const res = await api.post('/algorithms', payload);
                            handleAlgorithmCreated(createAlgorithmForRow, res.data);
                        } catch (err) {
                            alert('Failed to create algorithm: ' + (err.response?.data?.msg || err.message));
                        }
                    }}
                />
            )}
        </div>
    );
};

const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' },
    modal: { backgroundColor: '#FFFFFF', width: '900px', maxWidth: '95%', maxHeight: '90vh', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' },
    header: { padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' },
    closeBtn: { background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748B' },
    formContent: { padding: '20px', overflowY: 'auto' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' },
    column: { display: 'flex', flexDirection: 'column', gap: '10px' },
    label: { fontSize: '0.85rem', fontWeight: 'bold', color: '#475569', textTransform: 'uppercase', marginTop: '5px' },
    input: { padding: '10px', borderRadius: '4px', border: '1px solid #CBD5E1', fontSize: '0.95rem', fontFamily: '"Inter", sans-serif', width: '100%', boxSizing: 'border-box' },
    footer: { padding: '20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '15px', backgroundColor: '#F8FAFC', marginTop: '20px' },
    cancelBtn: { padding: '10px 20px', border: '1px solid #CBD5E1', backgroundColor: '#FFFFFF', color: '#475569', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' },
    saveBtn: { padding: '10px 20px', border: 'none', backgroundColor: '#0D9488', color: '#FFFFFF', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' },
    btnSecondary: { backgroundColor: '#0D9488', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }
};

export default ActionModal;
